import type { FilterValue } from "@shopify/hydrogen-react/storefront-api-types";

/**
 * Extracts taxonomy value GID from a filter's input field
 */
export const extractTaxonomyValueGID = (
  filterValue: FilterValue
): string | null => {
  try {
    if (typeof filterValue.input !== "string") {
      return null;
    }
    const input = JSON.parse(filterValue.input);
    return (
      (typeof input === "object" &&
        input !== null &&
        input.taxonomyMetafield?.value) ||
      null
    );
  } catch {
    return null;
  }
};

/**
 * Mapping of taxonomy GIDs to their canonical color names.
 * This mapping can be built dynamically from FilterValue objects or
 * maintained statically for common colors.
 *
 * Format: "gid://shopify/TaxonomyValue/{id}" -> "Color Name"
 */
type TaxonomyGIDToColorMap = Map<string, string>;

/**
 * Mapping of color names to taxonomy GIDs.
 * Used to convert colorGroup metafield values (English color names)
 * to taxonomy GIDs for filtering comparison.
 *
 * Format: "Color Name" -> "gid://shopify/TaxonomyValue/{id}"
 */
type ColorNameToTaxonomyGIDMap = Map<string, string>;

/**
 * Static fallback mapping of color names to taxonomy GIDs.
 *
 * Rationale:
 * - Primary mappings are built dynamically from current collection filters to
 *   reflect locale-aware labels and live taxonomy values.
 * - This static map guarantees coverage for our known colors when a given
 *   filter set does not include them (e.g., sparse filter payloads).
 * - Shopify TaxonomyValue GIDs are stable; dynamic mappings override these
 *   entries whenever available.
 * - Consider auditing for newly introduced colors during development.
 *
 * Format: "Color Name" -> "gid://shopify/TaxonomyValue/{id}"
 */
const STATIC_COLOR_TO_GID_MAP = new Map<string, string>([
  ["Black", "gid://shopify/TaxonomyValue/1"],
  ["Blue", "gid://shopify/TaxonomyValue/2"],
  ["White", "gid://shopify/TaxonomyValue/3"],
  ["Gold", "gid://shopify/TaxonomyValue/4"],
  ["Chrome", "gid://shopify/TaxonomyValue/5"],
  ["Beige", "gid://shopify/TaxonomyValue/6"],
  ["Brown", "gid://shopify/TaxonomyValue/7"],
  ["Green", "gid://shopify/TaxonomyValue/9"],
  ["Orange", "gid://shopify/TaxonomyValue/10"],
  ["Pink", "gid://shopify/TaxonomyValue/11"],
  ["Purple", "gid://shopify/TaxonomyValue/12"],
  ["Red", "gid://shopify/TaxonomyValue/13"],
  ["Yellow", "gid://shopify/TaxonomyValue/14"],
]);

/**
 * Static fallback mapping of taxonomy GIDs to color names.
 * Reverse of STATIC_COLOR_TO_GID_MAP for GID-to-name lookups.
 */
const STATIC_GID_TO_COLOR_MAP = new Map<string, string>(
  Array.from(STATIC_COLOR_TO_GID_MAP.entries()).map(([name, gid]) => [
    gid,
    name,
  ])
);

/**
 * Builds a bidirectional mapping of color names and taxonomy GIDs from FilterValue objects.
 * This extracts both the GID-to-name and name-to-GID mappings from collection filter values.
 *
 * @param filterValues - Array of FilterValue objects from collection filters
 * @returns Object containing both gidToName and nameToGid mappings
 */
export const buildColorTaxonomyMappingsFromFilters = (
  filterValues: FilterValue[]
): {
  gidToName: TaxonomyGIDToColorMap;
  nameToGid: ColorNameToTaxonomyGIDMap;
} => {
  // Start with static fallback mappings
  const gidToName = new Map<string, string>(STATIC_GID_TO_COLOR_MAP);
  const nameToGid = new Map<string, string>(STATIC_COLOR_TO_GID_MAP);

  // Override with dynamic mappings from filter values (locale-aware)
  // This allows localized labels to take precedence when available
  for (const filterValue of filterValues) {
    const gid = extractTaxonomyValueGID(filterValue);
    if (gid && filterValue.label) {
      // Normalize color name for consistent lookup (case-insensitive)
      const normalizedName = filterValue.label.trim();
      gidToName.set(gid, normalizedName);
      nameToGid.set(normalizedName, gid);
    }
  }

  return { gidToName, nameToGid };
};

/**
 * Gets the taxonomy GID for a color name.
 * Used to convert colorGroup metafield values (English color names) to taxonomy GIDs
 * for comparison with applied color filters.
 *
 * @param colorName - Color name (e.g., "Blue", "Green", "Black")
 * @param nameToGidMap - Map of color name to taxonomy GID (from filter values)
 * @returns Taxonomy GID or null if not found
 */
export const getTaxonomyGIDFromColorName = (
  colorName: string,
  nameToGidMap?: ColorNameToTaxonomyGIDMap
): string | null => {
  if (!colorName) return null;

  // Normalize color name for lookup (trim and case-insensitive)
  const normalizedName = colorName.trim();

  // Use provided map if available (preferred - dynamic/locale-aware)
  if (nameToGidMap) {
    // Try exact match first
    if (nameToGidMap.has(normalizedName)) {
      return nameToGidMap.get(normalizedName) ?? null;
    }
    // Try case-insensitive match
    for (const [name, gid] of nameToGidMap.entries()) {
      if (name.toLowerCase() === normalizedName.toLowerCase()) {
        return gid;
      }
    }
  }

  // Fallback to static mapping (case-insensitive)
  for (const [name, gid] of STATIC_COLOR_TO_GID_MAP.entries()) {
    if (name.toLowerCase() === normalizedName.toLowerCase()) {
      return gid;
    }
  }

  return null;
};

/**
 * Gets the readable color name for a taxonomy GID.
 * First attempts to resolve from the provided filter values map,
 * then falls back to static mapping.
 *
 * @param gid - Taxonomy GID (e.g., "gid://shopify/TaxonomyValue/1")
 * @param colorMap - Optional map of GID to color name (from filter values)
 * @returns Color name or null if not found
 */
export const getColorNameFromTaxonomyGID = (
  gid: string,
  colorMap?: TaxonomyGIDToColorMap
): string | null => {
  if (!gid || !gid.startsWith("gid://shopify/TaxonomyValue/")) {
    return null;
  }

  // Use provided map if available (preferred - dynamic/locale-aware)
  if (colorMap && colorMap.has(gid)) {
    return colorMap.get(gid) ?? null;
  }

  // Fallback to static mapping
  if (STATIC_GID_TO_COLOR_MAP.has(gid)) {
    return STATIC_GID_TO_COLOR_MAP.get(gid) ?? null;
  }

  // Not found in any mapping
  return null;
};
